import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, Typography, Chip, Button, Avatar,
  Divider, TextField, MenuItem, Select, FormControl, InputLabel,
  Tab, Tabs, List, ListItem, ListItemIcon, ListItemText, IconButton, CircularProgress
} from '@mui/material';
import {
  ArrowBack, Edit, Phone, Email, Business, LocationOn,
  EventNote, Description, Receipt, Add, Send, Call, Note
} from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import ClientFormDialog from '../components/clients/ClientFormDialog';
import { useSnackbar } from 'notistack';

const ACTIVITY_ICONS = { call: <Call />, email: <Email />, meeting: <EventNote />, note: <Note />, quote: <Description />, invoice: <Receipt /> };
const ACTIVITY_LABELS = { call: 'Llamada', email: 'Email', meeting: 'Reunión', note: 'Nota', quote: 'Cotización', invoice: 'Factura' };

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [actType, setActType] = useState('note');
  const [actDesc, setActDesc] = useState('');
  const [addingAct, setAddingAct] = useState(false);

  const fetchClient = async () => {
    const { data } = await API.get(`/clients/${id}`);
    setClient(data);
    setLoading(false);
  };

  useEffect(() => { fetchClient(); }, [id]);

  const addActivity = async () => {
    if (!actDesc.trim()) return;
    setAddingAct(true);
    try {
      await API.post(`/clients/${id}/activities`, { type: actType, description: actDesc });
      setActDesc('');
      fetchClient();
      enqueueSnackbar('Actividad registrada', { variant: 'success' });
    } finally {
      setAddingAct(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  if (!client) return <Typography>Cliente no encontrado</Typography>;

  const avatarColor = ['#6B21A8', '#0E7490', '#065F46', '#92400E'][(client.name?.charCodeAt(0) || 0) % 4];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/clientes')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{client.name}</Typography>
          {client.businessName && <Typography variant="body2" color="text.secondary">{client.businessName}</Typography>}
        </Box>
        <Chip label={STATUS_LABELS[client.status]} color={STATUS_COLORS[client.status]} />
        <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)}>Editar</Button>
        <Button variant="contained" startIcon={<Description />} onClick={() => navigate(`/cotizaciones/nueva?client=${id}`)}>
          Nueva cotización
        </Button>
      </Box>

      <Grid container spacing={2.5}>
        {/* Left: info cards */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, background: avatarColor, fontSize: '1.4rem', fontWeight: 700 }}>
                  {client.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{client.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{client.rfc || 'Sin RFC'}</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {[
                { icon: <Email fontSize="small" />, label: client.email },
                { icon: <Phone fontSize="small" />, label: client.phone },
                { icon: <Business fontSize="small" />, label: client.seller?.name ? `Vendedor: ${client.seller.name}` : null },
                { icon: <LocationOn fontSize="small" />, label: [client.address?.street, client.address?.city, client.address?.state].filter(Boolean).join(', ') || null }
              ].filter(i => i.label).map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                  <Typography variant="body2">{item.label}</Typography>
                </Box>
              ))}
              {client.creditDays > 0 && (
                <Box sx={{ mt: 2, p: 1.5, background: 'rgba(107,33,168,0.05)', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Crédito:</Typography>
                  <Typography variant="body2" fontWeight={700}>{client.creditDays} días</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {client.notes && (
            <Card>
              <CardContent>
                <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing="0.1em" color="primary.main">Notas</Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{client.notes}</Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: tabs */}
        <Grid item xs={12} md={8}>
          <Card>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #F3F4F6', px: 2 }}>
              <Tab label="Actividad CRM" />
              <Tab label="Cotizaciones" />
              <Tab label="Facturas" />
            </Tabs>

            {tab === 0 && (
              <CardContent>
                {/* Add activity */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select value={actType} onChange={e => setActType(e.target.value)}>
                      {Object.entries(ACTIVITY_LABELS).slice(0, 4).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField fullWidth size="small" placeholder="Descripción de la actividad…"
                    value={actDesc} onChange={e => setActDesc(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addActivity()} />
                  <Button variant="contained" onClick={addActivity} disabled={addingAct || !actDesc.trim()}>
                    {addingAct ? <CircularProgress size={18} /> : <Add />}
                  </Button>
                </Box>

                <List disablePadding>
                  {client.activities?.map((act, idx) => (
                    <ListItem key={idx} sx={{ px: 0, alignItems: 'flex-start' }} divider={idx < client.activities.length - 1}>
                      <ListItemIcon sx={{ mt: 0.5, minWidth: 36, color: 'primary.main' }}>
                        {ACTIVITY_ICONS[act.type] || <Note />}
                      </ListItemIcon>
                      <ListItemText
                        primary={act.description}
                        secondary={`${ACTIVITY_LABELS[act.type] || act.type} · ${fmt.dateTime(act.date)}${act.user?.name ? ` · ${act.user.name}` : ''}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  {(!client.activities || client.activities.length === 0) && (
                    <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                      Sin actividades registradas
                    </Typography>
                  )}
                </List>
              </CardContent>
            )}

            {tab === 1 && (
              <CardContent>
                <Button size="small" variant="contained" startIcon={<Add />}
                  onClick={() => navigate(`/cotizaciones/nueva?client=${id}`)} sx={{ mb: 2 }}>
                  Nueva cotización
                </Button>
                <ClientDocumentsList type="quotes" clientId={id} />
              </CardContent>
            )}

            {tab === 2 && (
              <CardContent>
                <ClientDocumentsList type="invoices" clientId={id} />
              </CardContent>
            )}
          </Card>
        </Grid>
      </Grid>

      <ClientFormDialog open={editOpen} client={client} onClose={() => setEditOpen(false)} onSave={() => { setEditOpen(false); fetchClient(); }} />
    </Box>
  );
}

function ClientDocumentsList({ type, clientId }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    API.get(`/${type}`, { params: { client: clientId } }).then(r => setDocs(type === 'quotes' ? r.data.quotes : r.data.invoices));
  }, [type, clientId]);

  return (
    <Box>
      {docs.map(d => (
        <Box key={d._id} sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: 1.5, borderRadius: 2, mb: 1, background: 'rgba(107,33,168,0.03)',
          cursor: 'pointer', '&:hover': { background: 'rgba(107,33,168,0.06)' }
        }}
          onClick={() => navigate(`/${type}/${d._id}/editar`)}
        >
          <Box>
            <Typography variant="body2" fontWeight={700} color="primary.main">{d.number}</Typography>
            <Typography variant="caption" color="text.secondary">{fmt.date(d.date || d.createdAt)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" fontWeight={700}>{fmt.currency(d.total)}</Typography>
            <Chip label={STATUS_LABELS[d.status]} color={STATUS_COLORS[d.status]} size="small" />
          </Box>
        </Box>
      ))}
      {docs.length === 0 && <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>Sin documentos</Typography>}
    </Box>
  );
}
